'use client';
import {useEffect, useState, useRef, useCallback} from 'react';
import MainLiveApi from '@/api/main/main-live';
import {getTimeStamp, getUserCookie, isLogin, sendWebUvLog} from '@/utils/main';
import {LIVE_INIT_LIST, MAIN_LIST_CNT} from '@/constants/main';
import {LiveGroupList} from '@/components/main/live/LiveGroupList';
import {LiveMenu} from '@/components/main/live/LiveMenu';
import {LiveGroupCategoryList} from '@/components/main/live/group/LiveGroupCategoryList';
import {LiveSportsSchedule} from '@/components/main/live/group/sports/LiveSportsSchedule';
import {LiveGroupBothBanner} from '@/components/main/live/group/LiveGroupBothBanner';
import {useNavigate} from 'react-router-dom';
import {Banner} from '@/components/common';
import {useHiddenStreamer} from '../../common/hooks/useHiddenStreamer';
import useGlobalUIStore from '@/stores';

export const LiveGroup = ({liveType, categoryName = ''}) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('view_cnt');
  const [selectedMenu, setSelectedMenu] = useState('');
  const [selectedCategory, setSelectedCategory] = useState({
    active: '',
    categoryNo: '',
    categoryName: categoryName,
  });

  const [hiddenStreamer, setHiddenStreamer] = useState([]);
  const [mainListInfo, setMainListInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [broadList, setBroadList] = useState(LIVE_INIT_LIST);
  const {
    remainCount,
    slicedBroadList,
    setRemainCount,
    filterBroadList,
    setSlicedBroadList,
    handleOnClickHiddenStreamer,
  } = useHiddenStreamer('LIVE', selectedMenu);
  const navigate = useNavigate();
  const {refreshTimestamp, setRefreshTimestamp} = useGlobalUIStore();

  const categoryList = useRef([]);

  /**
   * 카테고리 이름과 번호를 사용해 해당 카테고리 아이템의 ref를 찾는다.
   * @param {*} cateName 카테고리 이름
   * @param {*} cateNo 카테고리 번호
   * @returns
   */
  const findCategory = useCallback(
    (cateName, cateNo = null) => {
      if (!cateNo) {
        // 카테고리 번호 미사용 시
        return categoryList.current?.find(
          (category) => category.cateName === cateName,
        );
      } else {
        // 카테고리 번호 사용 시
        return categoryList.current?.find(
          (category) =>
            category.cateName === cateName && category.broadCateNo === cateNo,
        );
      }
    },
    [categoryList],
  );

  useEffect(() => {
    getStaticBroadListMainInfo();
  }, []);

  useEffect(() => {
    // URL 수준에서 라이브 유형 라우트 (/game, /bora, /sports) 변경 시 반응
    setSelectedMenu(liveType);
  }, [liveType]);

  useEffect(() => {
    // 라이브 메뉴 (게임, 보이는 라디오, 스포츠) 클릭 시 반응
    if (liveType === selectedMenu) {
      handleInitState();
      setSelectedCategory({
        active: '',
        categoryNo: '',
        categoryName: '',
      });
      getCategoryJsonData();
    }
  }, [selectedMenu]);

  useEffect(() => {
    if (remainCount > 0) {
      setPage((prev) => prev + 1);
    }
  }, [remainCount]);

  useEffect(() => {
    // 방송 목록 영역 -> 더 보기 클릭 시 반응
    if (page === 1) {
      setIsLoading(true);
    }
    if (broadList.isMore && selectedCategory.active !== '') {
      if (isLogin()) {
        getHiddenStreamerList();
      } else {
        loadBroadList(
          selectedCategory.categoryName,
          selectedCategory.categoryNo,
        );
      }
    } else {
      if (slicedBroadList.length > 0) {
        setBroadList({
          ...broadList,
          list: [...broadList.list, ...slicedBroadList],
        });
        setSlicedBroadList([]);
      } else {
        if (
          page === 1 &&
          remainCount === 0 &&
          selectedCategory.categoryNo.length > 0
        ) {
          if (isLogin()) {
            getHiddenStreamerList();
          } else {
            loadBroadList(
              selectedCategory.categoryName,
              selectedCategory.categoryNo,
            );
          }
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [page, order, selectedCategory, refreshTimestamp]);

  useEffect(() => {
    if (hiddenStreamer.length > 0) {
      loadBroadList(selectedCategory.categoryName, selectedCategory.categoryNo);
    }
  }, [hiddenStreamer]);

  useEffect(() => {
    // URL의 ?categoryName 파라미터 변경 시 반응
    // 같은 리액터 라우터 내에서 뒤로가기를 하다보니 이전 상태값들이 갱신이 되지않아서 도메인은 바뀌는것처럼 보이지만 뒤로가기에 맞는 리스트들이 노출되지않아서 도메인에 맞는 카테고리를 set시켜서 랜더링
    const updateSelectedCategory = (active, categoryNo, categoryName) => {
      setSelectedCategory({active, categoryNo, categoryName});
    };

    if (categoryList.current && selectedCategory.active === 'poster') {
      const beforeCategory = findCategory(categoryName);

      if (beforeCategory) {
        if (beforeCategory.cateName !== selectedCategory.categoryName) {
          updateSelectedCategory(
            'poster',
            beforeCategory.broadCateNo,
            categoryName,
          );
        }
      } else {
        updateSelectedCategory(
          selectedMenu,
          categoryList.current
            .map((category) => category.broadCateNo)
            .join(','),
          '',
        );
      }
    } else {
      const beforeCategory = findCategory(categoryName);
      if (beforeCategory) {
        if (beforeCategory.cateName !== selectedCategory.categoryName) {
          updateSelectedCategory(
            'poster',
            beforeCategory.broadCateNo,
            categoryName,
          );
        }
      }
    }
    handleInitState();
  }, [categoryName]);

  useEffect(() => {
    if (refreshTimestamp) {
      handleInitState();
    }
    window.scrollTo(0, 0);
  }, [refreshTimestamp]);

  const handleOnClickMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  const refreshTimer = useRef(false);
  const handleOnClickLiveMenu = (menuId) => {
    // 선택된 카테고리가 현재 메뉴와 같다면, 1.5초 동안 클릭 차단
    if (selectedCategory.active === menuId) {
      // refreshTimer가 true이면 함수 종료
      if (refreshTimer.current) {
        return;
      }
      setRefreshTimestamp();
      sendWebUvLog(null);
      refreshTimer.current = true;
      setTimeout(() => {
        refreshTimer.current = false;
      }, 1500);
      return; // 이 시점에서 함수 종료 (다른 로직을 타지 않도록)
    }
    // 메뉴가 'all'일 경우의 처리
    if (menuId === 'all') {
      navigate(`/live/${menuId}`);
    } else {
      // 다른 메뉴 선택 시의 처리
      handleInitState();
      setSelectedMenu(menuId);
      if (selectedMenu === menuId) {
        setSelectedCategory({
          active: selectedMenu,
          categoryNo: categoryList.current
            .map((category) => category.broadCateNo)
            .join(','),
          categoryName: '',
        });
      } else {
        setSelectedCategory({
          active: '',
          categoryNo: '',
          categoryName: '',
        });
      }
      navigate(`/live/group/${menuId}`);
    }
  };

  /**
   * 게임, 보이는 라디오, 스포츠 메뉴에서 카테고리 클릭 이벤트.
   * 선택한 카테고리 정보 상태를 업데이트 한다.
   * @param {*} e
   * @param {*} broadCateNo
   * @param {*} broadCateName
   */
  const handleOnClickPosterCategory = (e, broadCateNo, broadCateName) => {
    e.preventDefault();
    if (
      broadCateNo === selectedCategory.categoryNo &&
      broadCateName === selectedCategory.categoryName
    ) {
      // 선택한 카테고리를 반복선택 하는 경우 방어 로직
      // refreshTimer가 true이면 함수 종료
      if (refreshTimer.current) {
        return;
      }
      setRefreshTimestamp();
      sendWebUvLog(null);
      refreshTimer.current = true;
      setTimeout(() => {
        refreshTimer.current = false;
      }, 1500);
    }
    handleInitState();
    setSelectedCategory({
      active: 'poster',
      categoryNo: broadCateNo,
      categoryName: broadCateName,
    });
    navigate(
      `/live/group/${selectedMenu}?categoryName=${encodeURIComponent(broadCateName)}`,
    );
  };

  const handleOnClickSort = (type) => {
    if (type === order) {
      return;
    }
    handleInitState(type);
  };

  const handleInitState = (order = 'view_cnt') => {
    setPage(1);
    setOrder(order);
    setRemainCount(0);
    setSlicedBroadList([]);
    setBroadList(LIVE_INIT_LIST);
  };

  /**
   * 선택한 카테고리 아이템의 상태를 통해 해당 카테고리에 포함되는 방송 목록을 불러온다.
   * @param {string} selectedCategoryName 선택한 카테고리 이름
   * @param {string} selectedCategoryNo 선택한 카테고리 번호
   * @returns
   */
  const loadBroadList = async (selectedCategoryName, selectedCategoryNo) => {
    try {
      if (!selectedCategoryNo) {
        // 카테고리 번호가 주어지지 않을 경우 X
        return;
      }

      /**
       * 선택한 카테고리 조건에 부합하는 카테고리 아이템 ref 객체
       * 선택한 조건에 부합하는 아이템이 없으면 null
       * API에는 selectedCategoryNo 기반으로 질의
       */
      const selectedCategoryItem = findCategory(
        selectedCategoryName,
        selectedCategoryNo,
      );

      let queryParams = {
        selectType: 'cate',
        selectValue: selectedCategoryNo, // API에는 카테고리 번호를 사용
        orderType: order,
        pageNo: page,
        lang: 'ko_KR',
        _: getTimeStamp(),
      };

      // 요청 파라미터의 szActionType이 3일땐 연령제한 방송만 가져온다.
      queryParams.szActionType =
        !!selectedCategoryItem && selectedCategoryItem.isAdult ? 3 : 2;

      const response = await MainLiveApi.getBroadList(queryParams);

      if (response && response.broad) {
        const {totalCnt, broad} = response;
        if (Number(totalCnt) > 0 && broad.length > 0) {
          const totalPage = Math.ceil(Number(totalCnt) / MAIN_LIST_CNT);
          const isMore = Number(page) < Number(totalPage) ? true : false;
          if (hiddenStreamer.length > 0) {
            filterBroadList(
              'live',
              page,
              broad,
              isMore,
              hiddenStreamer,
              setBroadList,
            );
          } else {
            setBroadList({
              isMore: isMore,
              list: [...broadList.list, ...broad],
            });
          }
        }
      }
    } catch (error) {
      setBroadList(LIVE_INIT_LIST);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 숨김 처리된 스트리머 목록을 가져와 방송 목록에 반영한다.
   */
  const getHiddenStreamerList = async () => {
    try {
      const response = await MainLiveApi.getHiddenStreamerList();
      if (response && response.result === 1) {
        if (response.data.length === 0) {
          loadBroadList(
            selectedCategory.categoryName,
            selectedCategory.categoryNo,
          );
        } else {
          setHiddenStreamer([...response.data]);
        }
      }
    } catch (error) {
      loadBroadList(selectedCategory.categoryName, selectedCategory.categoryNo);
    }
  };

  /**
   * 정적 핫이슈, 배너정보 등을 가져온다.
   */
  const getStaticBroadListMainInfo = async () => {
    try {
      const response = await MainLiveApi.getStaticBroadListMainInfo();

      if (response) {
        setMainListInfo(response);
      }
    } catch (error) {
      setMainListInfo({});
    }
  };

  /**
   * 현재 라이브 유형에 해당하는 카테고리 정보를 가져온다.
   */
  const getCategoryJsonData = async () => {
    try {
      let response;

      if (liveType === 'award2024') {
        response = {
          data: [
            {
              broadCateNo: '00950000',
            },
          ],
        };
      } else {
        response = await MainLiveApi.getJsonData(
          `/main/main_${liveType}category_viewer.js`,
        );
      }

      if (response) {
        const formattedCategoryList = response.data.map((item) => {
          if (item.broadCateNo) {
            item.broadCateNo = item.broadCateNo
              .padStart(8, '0')
              .replace(/\s+/g, '');
          }
          return item;
        });
        categoryList.current = formattedCategoryList;
        if (categoryName) {
          setSelectedCategory({
            active: 'poster',
            categoryNo: formattedCategoryList
              .filter((category) => category.cateName === categoryName)
              .map((category) => category.broadCateNo)
              .join(','),
            categoryName: categoryName,
          });
        } else {
          if (formattedCategoryList.length) {
            setSelectedCategory({
              active: liveType,
              categoryNo: formattedCategoryList
                .map((category) => category.broadCateNo)
                .join(','),
              categoryName: formattedCategoryList.categoryName,
            });
          } else {
            //카테고리 리스트를 받아오지 못헀을경우에, 강제로 해당 카테고리의 전체 리스트를 그려준다
            setSelectedCategory({
              active: liveType,
              categoryNo: `${liveType}_all`,
              categoryName: '',
            });
          }
        }
      }
    } catch (error) {
      //API 에러로 인해 카테고리 리스트를 받아오지 못헀을경우에, 강제로 해당 카테고리의 전체 리스트를 그려준다
      setSelectedCategory({
        active: liveType,
        categoryNo: `${liveType}_all`,
        categoryName: '',
      });
      categoryList.current = [];
    }
  };

  const liveMenuProps = {
    selectedMenu,
    handleOnClickLiveMenu,
  };

  const liveCategoryListProps = {
    categoryList: categoryList.current,
    selectedCategory,
    handleOnClickPosterCategory,
  };

  const liveGroupBothBannerProps = {
    selectedMenu,
    mainListInfo,
    selectedCategory,
  };

  const liveSportsScheduleProps = {
    mainListInfo,
  };

  const liveListProps = {
    order,
    broadList,
    isLoading,
    selectedMenu,
    selectedTitle: selectedCategory.categoryName,
    slicedBroadList,
    location: selectedMenu,
    handleOnClickMore,
    handleOnClickSort,
    handleOnClickHiddenStreamer,
  };

  return (
    <>
      {selectedMenu !== 'award2024' && <LiveMenu {...liveMenuProps} />}
      <div id='container' className='_live'>
        {selectedMenu !== 'award2024' && (
          <LiveGroupCategoryList {...liveCategoryListProps} />
        )}
        {selectedMenu === 'sports' && (
          <LiveSportsSchedule {...liveSportsScheduleProps} />
        )}
        <Banner
          type={'sub'}
          category={selectedMenu}
          posterCategory={selectedCategory}
        />
        <LiveGroupBothBanner {...liveGroupBothBannerProps} />
        <LiveGroupList {...liveListProps} />
      </div>
    </>
  );
};
